import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner, Input } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  getInvoiceVendors,
  createInvoiceVendor,
  updateInvoiceVendor,
  deleteInvoiceVendor,
  getInvoiceCategories,
} from "./Api";

const FInput = ({ value, onChange, placeholder, type = "text" }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ borderColor: "#E2E8F0", borderRadius: 7, fontSize: "0.82rem" }}
  />
);

const InlineVendorRow = ({ data, isNew, categories, onSave, onCancel }) => {
  const [row, setRow] = useState({ isActive: true, email: "", phone: "", ...data });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error("Vendor name is required"); return; }
    if (!row.categoryId) { toast.error("Please choose a category"); return; }
    setSaving(true);
    try { await onSave(row); } finally { setSaving(false); }
  };

  return (
    <tr style={{ background: isNew ? "#ECFDF5" : "#EEF2FF" }}>
      <td style={{ width: 40, textAlign: "center", padding: "8px 10px" }}>
        <FeatherIcon icon={isNew ? "plus" : "edit-2"} size={12} color={isNew ? "#059669" : "#4F46E5"} />
      </td>
      <td style={{ minWidth: 170, padding: "8px 10px" }}>
        <FInput value={row.name} onChange={set("name")} placeholder="Vendor name…" />
      </td>
      <td style={{ minWidth: 150, padding: "8px 10px" }}>
        <select
          value={row.categoryId || ""}
          onChange={(e) => set("categoryId")(e.target.value)}
          style={{
            border: "1px solid #E2E8F0", borderRadius: 7, fontSize: "0.8rem",
            padding: "5px 8px", width: "100%", outline: "none", background: "#fff",
          }}
        >
          <option value="">Choose category…</option>
          {categories.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
      </td>
      <td style={{ minWidth: 170, padding: "8px 10px" }}>
        <FInput value={row.email} onChange={set("email")} placeholder="vendor@email.com" type="email" />
      </td>
      <td style={{ minWidth: 130, padding: "8px 10px" }}>
        <FInput value={row.phone} onChange={set("phone")} placeholder="Phone (optional)" />
      </td>
      <td style={{ width: 100, textAlign: "center", padding: "8px 10px" }}>
        {isNew ? (
          <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>Active</span>
        ) : (
          <select
            value={row.isActive ? "true" : "false"}
            onChange={(e) => set("isActive")(e.target.value === "true")}
            style={{
              border: "1px solid #E2E8F0", borderRadius: 7, fontSize: "0.78rem",
              padding: "4px 8px", cursor: "pointer", outline: "none",
              background: row.isActive ? "#ECFDF5" : "#FEF2F2",
              color: row.isActive ? "#059669" : "#DC2626",
              fontWeight: 700, width: "100%",
            }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </td>
      <td style={{ width: 130, textAlign: "center", padding: "8px 10px" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? "Add" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InvoiceVendorsTable = ({ userRole, onRefresh }) => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const isCompany = (userRole || "").toLowerCase() === "company";

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getInvoiceCategories({ activeOnly: "true" });
      setCategories(res?.data || []);
    } catch { /* handled */ }
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceVendors(showInactive ? {} : { activeOnly: "true" });
      setVendors(res?.data || []);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleCreate = async (row) => {
    await createInvoiceVendor({
      name: row.name.trim(),
      categoryId: row.categoryId,
      email: row.email?.trim() || "",
      phone: row.phone?.trim() || "",
    });
    setShowNewRow(false);
    fetchVendors();
    onRefresh?.();
  };

  const handleUpdate = async (id, row) => {
    await updateInvoiceVendor(id, {
      name: row.name.trim(),
      categoryId: row.categoryId,
      email: row.email?.trim() || "",
      phone: row.phone?.trim() || "",
      isActive: row.isActive,
    });
    setEditingId(null);
    fetchVendors();
    onRefresh?.();
  };

  const handleDelete = async (v) => {
    const res = await Swal.fire({
      title: "Deactivate Vendor?",
      html: `<p>Deactivate <strong>${v.name}</strong>? It will no longer appear when creating invoices.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Deactivate",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try { await deleteInvoiceVendor(v._id); fetchVendors(); onRefresh?.(); } catch { /* handled */ }
  };

  const colSpan = isCompany ? 7 : 6;

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="briefcase" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Vendors</div>
          </div>
        </div>
        <div className="iv-header__actions" style={{ gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", fontWeight: 600, cursor: "pointer" }}>
            <Input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ marginTop: 0 }} />
            Show inactive
          </label>
          {isCompany && (
            <button type="button" className="iv-btn iv-btn--primary" onClick={() => setShowNewRow(true)} disabled={showNewRow || categories.length === 0}>
              <FeatherIcon icon="plus" size={14} /> New Vendor
            </button>
          )}
        </div>
      </div>

      {isCompany && categories.length === 0 && (
        <div style={{ padding: "10px 16px", background: "#FFFBEB", color: "#B45309", fontSize: "0.8rem", borderBottom: "1px solid #FDE68A" }}>
          Add at least one Category first — every vendor must belong to a category.
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table className="iv-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
              <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Category</th>
              <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Email</th>
              <th style={{ minWidth: 130, textAlign: "left", paddingLeft: 10 }}>Phone</th>
              <th style={{ width: 100 }}>Status</th>
              {isCompany && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineVendorRow
                isNew
                data={{ name: "", categoryId: "", email: "", phone: "" }}
                categories={categories}
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
            )}

            {!loading && vendors.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={colSpan} className="iv-empty-cell">
                  <div className="iv-empty" style={{ minHeight: "20vh" }}>
                    <div className="iv-empty__icon-wrap"><FeatherIcon icon="briefcase" size={22} color="#4F46E5" /></div>
                    <div className="iv-empty__title">No Vendors</div>
                    <div className="iv-empty__sub">{isCompany ? "Add your first vendor to start creating invoices." : "Ask a company admin to add vendors."}</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && vendors.map((v) => {
              if (editingId === v._id) {
                return (
                  <InlineVendorRow
                    key={v._id}
                    data={{ ...v, categoryId: v.categoryId || v.category?._id || "" }}
                    categories={categories}
                    onSave={(row) => handleUpdate(v._id, row)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr key={v._id} className="iv-row">
                  <td style={{ textAlign: "center", width: 40 }}><FeatherIcon icon="briefcase" size={13} color="#94A3B8" /></td>
                  <td style={{ textAlign: "left", paddingLeft: 10, fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{v.name}</td>
                  <td style={{ textAlign: "left", paddingLeft: 10 }}>
                    <span style={{ display: "inline-block", background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE", borderRadius: 6, padding: "2px 9px", fontSize: "0.74rem", fontWeight: 600 }}>
                      {v.category?.name || "—"}
                    </span>
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>{v.email || <span style={{ color: "#CBD5E1" }}>—</span>}</td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>{v.phone || <span style={{ color: "#CBD5E1" }}>—</span>}</td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{
                      background: v.isActive ? "#ECFDF5" : "#FEF2F2",
                      color: v.isActive ? "#059669" : "#DC2626",
                      border: v.isActive ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                      borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
                    }}>
                      {v.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isCompany && (
                    <td style={{ textAlign: "center" }}>
                      <div className="iv-action-group">
                        <button type="button" className="iv-ebtn" title="Edit" onClick={() => setEditingId(v._id)}>
                          <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                        </button>
                        {v.isActive && (
                          <button type="button" className="iv-dbtn" title="Deactivate" onClick={() => handleDelete(v)}>
                            <FeatherIcon icon="x-circle" size={13} color="#DC2626" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InvoiceVendorsTable };
